import { VoiceSettingsTab } from "../../voice/VoiceSettingsTab"
/**
 * SettingsModal
 *
 * Main settings modal with sidebar navigation for the Code module.
 * Flat, square design following Theme V2.
 */

import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { BarChart3, Bell, Bug, Globe2, Mic, Palette, Plug, Settings, Sparkles, Terminal, Users, X } from "lucide-react"
import { observer } from "mobx-react"
import { useMemo, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { useMediaQuery } from "../../hooks/useMediaQuery"
import { getPlatformAdapter } from "../../platform/adapter"
import type { CodeStore } from "../../store/store"
import { ScrollArea } from "../ui/ScrollArea"
import { Select } from "../ui/Select"
import { useModalLayer } from "../ui/modalLayers"
import { Z_INDEX } from "../../constants"
import { ActionsTab } from "../actions/ActionsTab"
import { AppearanceTab } from "./AppearanceTab"
import { ConnectorsTab } from "./ConnectorsTab"
import { DevTab, isLocalDev } from "./DevTab"
import { MobileNotificationsTab } from "./MobileNotificationsTab"
import { StatsTab } from "./StatsTab"
import { SystemConfigTab } from "./SystemConfigTab"
import { BrowserSettingsTab } from "./BrowserSettingsTab"
import { SharingTab } from "../sharing/SharingTab"
import { hasEmbeddedBrowser } from "../../electronAPIs/browser"

export type SettingsTab = "actions" | "appearance" | "voice" | "browser" | "connectors" | "connections" | "notifications" | "system" | "stats" | "dev"

interface TabConfig {
    id: SettingsTab
    label: string
    icon: typeof Settings
    devOnly?: boolean
    nativeMobileOnly?: boolean
    embeddedBrowserOnly?: boolean
}

const ALL_TABS: TabConfig[] = [
    { id: "voice", label: "Voice", icon: Mic },
    { id: "actions", label: "Actions", icon: Sparkles },
    { id: "appearance", label: "Vibes", icon: Palette },
    { id: "browser", label: "Browser", icon: Globe2, embeddedBrowserOnly: true },
    { id: "connectors", label: "Connectors", icon: Plug },
    { id: "connections", label: "Sharing", icon: Users },
    { id: "notifications", label: "Notifications", icon: Bell, nativeMobileOnly: true },
    { id: "system", label: "System", icon: Terminal },
    { id: "stats", label: "Stats", icon: BarChart3 },
    { id: "dev", label: "Dev", icon: Bug, devOnly: true },
]

export function isSettingsTabAvailable(tabId: SettingsTab, environment: { showDevTab: boolean; nativeMobile: boolean; embeddedBrowser: boolean }): boolean {
    const tab = ALL_TABS.find((candidate) => candidate.id === tabId)
    return (
        tab !== undefined &&
        (!tab.devOnly || environment.showDevTab) &&
        (!tab.nativeMobileOnly || environment.nativeMobile) &&
        (!tab.embeddedBrowserOnly || environment.embeddedBrowser)
    )
}

interface SettingsModalProps {
    store: CodeStore
    /** Optional tab to open. If not provided, opens to the last-used tab (or "connectors" as fallback). */
    initialTab?: SettingsTab
}

export const SettingsModal = NiceModal.create(
    observer(({ store, initialTab }: SettingsModalProps) => {
        const modal = useModal()
        const compact = useMediaQuery("(max-width: 900px)")
        const showDevTab = useMemo(() => isLocalDev(), [])
        const nativeMobile = useMemo(() => {
            const kind = getPlatformAdapter().kind
            return kind === "ios" || kind === "android"
        }, [])
        const embeddedBrowser = useMemo(() => hasEmbeddedBrowser(), [])
        const tabs = useMemo(
            () => ALL_TABS.filter((tab) => isSettingsTabAvailable(tab.id, { showDevTab, nativeMobile, embeddedBrowser })),
            [embeddedBrowser, nativeMobile, showDevTab]
        )
        const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
            const preferredTab = initialTab ?? store.personalSettingsStore?.settings.current.lastSettingsTab ?? "connectors"
            return tabs.some((tab) => tab.id === preferredTab) ? preferredTab : "connectors"
        })

        const handleTabChange = (tab: SettingsTab) => {
            setActiveTab(tab)
            // Don't persist dev tab as last tab
            if (tab !== "dev") {
                store.personalSettingsStore?.settings.set({ lastSettingsTab: tab })
            }
        }

        const isTopLayer = useModalLayer()
        useHotkeys(
            "esc",
            (e) => {
                // A sheet opened from a tab (share, access, link) stacks above
                // Settings; Escape closes only that sheet, not both.
                if (!isTopLayer()) return
                e.preventDefault()
                e.stopPropagation()
                modal.remove()
            },
            { enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"] },
            [modal, isTopLayer]
        )

        const handleBackdropClick = (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) {
                modal.remove()
            }
        }

        const tabButtonClass = (isActive: boolean) =>
            `btn w-full flex items-center gap-2 p-2.5 px-3 text-left text-sm transition-colors ${
                isActive
                    ? "bg-primary/10 text-primary border-l-2 border-primary"
                    : "bg-transparent text-muted hover:text-base-content hover:bg-base-200 border-l-2 border-transparent"
            }`

        const tabEntries = tabs.map((tab) => ({
            id: tab.id,
            content: (
                <span className="flex min-w-0 items-center gap-2">
                    <tab.icon size={16} className="shrink-0" />
                    <span className="truncate">{tab.label}</span>
                </span>
            ),
        }))

        const renderTabContent = () => {
            switch (activeTab) {
                case "voice":
                    return <VoiceSettingsTab store={store} onStart={() => modal.remove()} />
                case "actions":
                    return <ActionsTab store={store} />
                case "appearance":
                    return <AppearanceTab store={store} />
                case "browser":
                    return <BrowserSettingsTab />
                case "connectors":
                    return <ConnectorsTab store={store} />
                case "connections":
                    return <SharingTab />
                case "notifications":
                    return <MobileNotificationsTab />
                case "system":
                    return <SystemConfigTab store={store} />
                case "stats":
                    return <StatsTab store={store} />
                case "dev":
                    return <DevTab store={store} />
                default:
                    return null
            }
        }

        return (
            <div
                className={`absolute inset-0 bg-black/50 flex justify-center ${
                    compact ? "items-stretch p-0 min-[600px]:items-center min-[600px]:p-4" : "items-start p-4 pb-24"
                }`}
                style={
                    compact
                        ? { zIndex: Z_INDEX.GLOBAL_MODAL, backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)" }
                        : {
                              zIndex: Z_INDEX.GLOBAL_MODAL,
                              backdropFilter: "blur(5px)",
                              WebkitBackdropFilter: "blur(5px)",
                              paddingTop: "max(min(80px, 15%), 1rem)",
                          }
                }
                onClick={handleBackdropClick}
            >
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="settings-title"
                    className={`bg-base-100 shadow-2xl w-full flex flex-col ${
                        compact
                            ? "h-full max-w-none border-0 min-[600px]:h-[calc(100dvh-2rem)] min-[600px]:max-h-[760px] min-[600px]:max-w-[720px] min-[600px]:border min-[600px]:border-border"
                            : "max-w-5xl border border-border"
                    }`}
                    style={compact ? undefined : { height: "min(760px, 85vh)" }}
                >
                    {/* Header */}
                    <div
                        className={`flex items-center justify-between border-b border-border flex-shrink-0 ${compact ? "px-4 pb-2" : "p-4"}`}
                        style={compact ? { paddingTop: "max(0.5rem, env(safe-area-inset-top))" } : undefined}
                    >
                        <div className="flex items-center gap-2">
                            <Settings size={18} className="text-muted" />
                            <h2 id="settings-title" className="text-lg font-semibold text-base-content">
                                Settings
                            </h2>
                        </div>
                        <button
                            type="button"
                            aria-label="Close Settings"
                            className={`btn flex items-center justify-center bg-transparent hover:bg-base-200 text-muted hover:text-base-content transition-colors ${
                                compact ? "w-11 h-11" : "w-8 h-8"
                            }`}
                            onClick={() => modal.remove()}
                            title="Close"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {compact && (
                        <div className="flex-shrink-0 border-b border-border bg-base-200/30 p-3">
                            <Select
                                selectedId={activeTab}
                                entries={tabEntries}
                                onSelect={(entry) => handleTabChange(entry.id)}
                                className={{
                                    trigger: "h-11 w-full bg-base-100 text-sm",
                                    popup: "z-[60]",
                                    item: "min-h-11 py-3",
                                    value: "min-w-0 flex-1",
                                }}
                            />
                        </div>
                    )}

                    {/* Content with Sidebar */}
                    <div className="flex flex-1 min-h-0 overflow-hidden">
                        {/* Sidebar */}
                        {!compact && (
                            <div className="w-48 flex-shrink-0 border-r border-border bg-base-200/30">
                                <nav className="flex flex-col p-2 gap-1">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => handleTabChange(tab.id)}
                                            className={tabButtonClass(activeTab === tab.id)}
                                        >
                                            <tab.icon size={16} />
                                            <span>{tab.label}</span>
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        )}

                        {/* Main Content */}
                        <div className="flex-1 min-w-0 min-h-0 overflow-hidden relative">
                            <ScrollArea className="absolute inset-0" viewportClassName={activeTab === "stats" ? "p-0" : compact ? "p-4" : "p-6"}>
                                {compact ? (
                                    <div className="min-h-full pb-[max(1rem,env(safe-area-inset-bottom))]">{renderTabContent()}</div>
                                ) : (
                                    renderTabContent()
                                )}
                            </ScrollArea>
                        </div>
                    </div>
                </div>
            </div>
        )
    })
)
